<template>
  <div class="bg-surface-elevated border border-gray-200 rounded-lg overflow-hidden">
    <header class="card-header flex items-center justify-between">
      <div class="flex items-center gap-2 min-w-0">
        <EditorIcon name="type" class="w-4 h-4 text-gray-500 flex-shrink-0" />
        <div class="min-w-0">
          <h2 class="font-medium text-gray-900 truncate">
            {{ form.name }}
          </h2>
          <p class="text-[11px] text-gray-500 truncate">
            Form · {{ form.slug }}<span v-if="multiStep"> · {{ stepCount }} steps</span>
          </p>
        </div>
      </div>
      <button
        type="button"
        class="btn btn-ghost btn-icon btn-xs flex-shrink-0"
        aria-label="Close form editor"
        title="Close form editor"
        @click="$emit('close')">
        <EditorIcon name="close" class="w-4 h-4" />
      </button>
    </header>

    <!-- Tabs -->
    <div class="tabs px-2" role="tablist">
      <button
        v-for="t in tabs"
        :key="t.id"
        type="button"
        role="tab"
        class="tab"
        :class="{ active: tab === t.id }"
        :aria-selected="tab === t.id"
        @click="tab = t.id">
        {{ t.label }}
      </button>
    </div>

    <!-- ===================== FIELDS ===================== -->
    <div v-show="tab === 'fields'" class="p-4">
      <div ref="fieldList" class="space-y-3">
        <div
          v-for="(field, index) in fields"
          :key="field._key"
          :data-field-row="field._key"
          class="border border-gray-200 rounded-md p-3">
          <div class="flex items-center justify-between mb-2">
            <div class="flex items-center gap-1.5 min-w-0">
              <span
                class="field-row-handle cursor-grab text-gray-400 hover:text-gray-600 select-none flex-shrink-0"
                title="Drag to reorder"
                aria-hidden="true"><EditorIcon name="drag" class="w-4 h-4" /></span>
              <button
                type="button"
                class="flex items-center gap-1 text-left rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 min-w-0"
                :aria-expanded="expanded.has(field._key)"
                @click="toggleExpand(field._key)">
                <EditorIcon :name="expanded.has(field._key) ? 'chevron-down' : 'chevron-right'" class="w-4 h-4 text-gray-500 flex-shrink-0" />
                <span class="text-sm font-medium text-gray-900 truncate">{{ field.label || field.id || 'Untitled field' }}</span>
                <span class="text-[10px] uppercase tracking-wider text-gray-400 flex-shrink-0">{{ field.type }}</span>
              </button>
            </div>
            <div class="flex items-center gap-1.5 flex-shrink-0">
              <button
                type="button"
                class="btn btn-ghost btn-icon btn-xs"
                title="Move field up"
                aria-label="Move field up"
                :disabled="index === 0"
                @click="moveField(index, -1)">
                <EditorIcon name="arrow-up" class="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                class="btn btn-ghost btn-icon btn-xs"
                title="Move field down"
                aria-label="Move field down"
                :disabled="index === fields.length - 1"
                @click="moveField(index, 1)">
                <EditorIcon name="arrow-down" class="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                class="btn btn-link btn-sm text-red-600"
                title="Remove field"
                @click="removeField(index)">
                Remove
              </button>
            </div>
          </div>

          <div v-if="expanded.has(field._key)" class="space-y-3 pt-1">
            <label class="block">
              <span class="form-label mb-1 block">Label</span>
              <input
                v-model="field.label"
                type="text"
                class="form-input w-full"
                @change="commit">
            </label>

            <p class="form-helper">
              Field key (immutable): <code class="text-gray-600">{{ field.id || '— assigned on save' }}</code>
            </p>

            <div class="grid grid-cols-2 gap-3">
              <label class="block">
                <span class="form-label mb-1 block">Type</span>
                <select v-model="field.type" class="form-select w-full" @change="onTypeChange(field)">
                  <option v-for="t in FIELD_TYPES" :key="t" :value="t">{{ t }}</option>
                </select>
              </label>
              <label class="block">
                <span class="form-label mb-1 block">Width</span>
                <select v-model="field.width" class="form-select w-full" @change="commit">
                  <option v-for="w in FIELD_WIDTHS" :key="w" :value="w">{{ w }}</option>
                </select>
              </label>
            </div>

            <div class="flex items-center gap-4">
              <label class="flex items-center gap-2 text-xs text-gray-700">
                <input
                  v-model="field.required"
                  type="checkbox"
                  class="form-checkbox"
                  @change="commit">
                Required
              </label>
              <label v-if="multiStep || field.step != null" class="flex items-center gap-2 text-xs text-gray-700">
                Step
                <input
                  v-model.number="field.step"
                  type="number"
                  min="0"
                  class="form-input w-16"
                  @change="commit">
              </label>
            </div>

            <label v-if="hasPlaceholder(field)" class="block">
              <span class="form-label mb-1 block">Placeholder</span>
              <input
                v-model="field.placeholder"
                type="text"
                class="form-input w-full"
                @change="commit">
            </label>

            <!-- Options (select / radio / checkbox_group) -->
            <div v-if="needsOptions(field)" class="space-y-2">
              <span class="form-label block">Options</span>
              <div
                v-for="(opt, oi) in field.options"
                :key="oi"
                class="flex items-center gap-2">
                <input
                  v-model="opt.label"
                  type="text"
                  placeholder="Label"
                  class="form-input flex-1"
                  @change="onOptionLabel(field, opt); commit()">
                <input
                  v-model="opt.value"
                  type="text"
                  placeholder="value"
                  class="form-input w-28"
                  @change="commit">
                <button
                  type="button"
                  class="btn btn-ghost btn-icon btn-xs text-gray-500 hover:text-red-600"
                  aria-label="Remove option"
                  @click="removeOption(field, oi)">
                  ×
                </button>
              </div>
              <button
                type="button"
                class="btn btn-link btn-sm"
                @click="addOption(field)">
                + Add option
              </button>
            </div>

            <!-- Conditional visibility (show_if) -->
            <div class="space-y-2 border-t border-gray-100 pt-3">
              <label class="flex items-center gap-2 text-xs text-gray-700">
                <input
                  type="checkbox"
                  :checked="!!field.show_if"
                  class="form-checkbox"
                  @change="toggleShowIf(field, $event.target.checked)">
                Only show this field when…
              </label>
              <div v-if="field.show_if" class="flex flex-wrap items-center gap-2 pl-6">
                <select v-model="field.show_if.field" class="form-select" @change="commit">
                  <option value="" disabled>
                    field…
                  </option>
                  <option v-for="o in otherFields(field)" :key="o.id" :value="o.id">
                    {{ o.label || o.id }}
                  </option>
                </select>
                <select v-model="field.show_if.op" class="form-select" @change="commit">
                  <option v-for="o in SHOW_IF_OPS" :key="o" :value="o">
                    {{ o }}
                  </option>
                </select>
                <input
                  v-if="!['present', 'blank'].includes(field.show_if.op)"
                  v-model="field.show_if.value"
                  type="text"
                  placeholder="value"
                  class="form-input w-32"
                  @change="commit">
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-3 flex flex-wrap gap-2">
        <button
          v-for="preset in ADD_PRESETS"
          :key="preset.type"
          type="button"
          class="btn btn-secondary btn-sm"
          @click="addField(preset.type)">
          + {{ preset.label }}
        </button>
      </div>
    </div>

    <!-- ===================== SETTINGS ===================== -->
    <div v-show="tab === 'settings'" class="p-4 space-y-5">
      <!-- Success -->
      <fieldset class="space-y-2">
        <legend class="text-xs font-semibold uppercase tracking-wider text-gray-500">
          After submit
        </legend>
        <label class="block">
          <span class="form-label mb-1 block">Success message</span>
          <textarea
            v-model="s.success_message"
            rows="2"
            class="form-input w-full"
            @change="saveSettings" />
        </label>
        <label class="block">
          <span class="form-label mb-1 block">Redirect URL (optional)</span>
          <input
            v-model="s.success_redirect_url"
            type="text"
            placeholder="https://…/thank-you"
            class="form-input w-full"
            @change="saveSettings">
        </label>
      </fieldset>

      <!-- Notifications -->
      <fieldset class="space-y-2">
        <legend class="text-xs font-semibold uppercase tracking-wider text-gray-500">
          Email notifications
        </legend>
        <label class="flex items-center gap-2 text-xs text-gray-700">
          <input v-model="s.email_enabled" type="checkbox" class="form-checkbox" @change="saveSettings">
          Notify recipients on each submission
        </label>
        <label class="block">
          <span class="form-label mb-1 block">Recipient emails (comma-separated)</span>
          <input
            v-model="s.recipient_emails"
            type="text"
            placeholder="you@business.com, team@business.com"
            class="form-input w-full"
            @change="saveSettings">
        </label>
        <label class="flex items-center gap-2 text-xs text-gray-700">
          <input v-model="s.send_customer_confirmation" type="checkbox" class="form-checkbox" @change="saveSettings">
          Send the customer a confirmation email
        </label>
      </fieldset>

      <!-- Spam -->
      <fieldset class="space-y-2">
        <legend class="text-xs font-semibold uppercase tracking-wider text-gray-500">
          Spam protection
        </legend>
        <label class="flex items-center gap-2 text-xs text-gray-700">
          <input v-model="s.honeypot_enabled" type="checkbox" class="form-checkbox" @change="saveSettings">
          Honeypot (invisible bot trap)
        </label>
        <label class="flex items-center gap-2 text-xs text-gray-700">
          <input v-model="s.turnstile_enabled" type="checkbox" class="form-checkbox" @change="saveSettings">
          Cloudflare Turnstile challenge
        </label>
        <label class="block">
          <span class="form-label mb-1 block">Rate limit (submissions per IP)</span>
          <input
            v-model.number="s.rate_limit_per_ip"
            type="number"
            min="1"
            class="form-input w-24"
            @change="saveSettings">
        </label>
      </fieldset>

      <!-- SMS -->
      <fieldset class="space-y-2">
        <legend class="text-xs font-semibold uppercase tracking-wider text-gray-500">
          Text alerts (SMS)
        </legend>
        <label class="flex items-center gap-2 text-xs text-gray-700">
          <input v-model="s.sms_enabled" type="checkbox" class="form-checkbox" @change="saveSettings">
          Enable SMS
        </label>
        <label class="block">
          <span class="form-label mb-1 block">Front-desk number (new-lead alert)</span>
          <input
            v-model="s.sms_front_desk_number"
            type="text"
            placeholder="+15551234567"
            class="form-input w-full"
            @change="saveSettings">
        </label>
        <label class="flex items-center gap-2 text-xs text-gray-700">
          <input v-model="s.sms_auto_response_enabled" type="checkbox" class="form-checkbox" @change="saveSettings">
          Auto-text the lead (requires consent)
        </label>
        <label class="block">
          <span class="form-label mb-1 block">Auto-response message</span>
          <textarea
            v-model="s.sms_auto_response_template"
            rows="2"
            class="form-input w-full"
            @change="saveSettings" />
        </label>
      </fieldset>

      <!-- Webhooks (read-only list) -->
      <fieldset class="space-y-2">
        <legend class="text-xs font-semibold uppercase tracking-wider text-gray-500">
          Webhooks
        </legend>
        <p v-if="!form.webhooks || form.webhooks.length === 0" class="text-xs text-gray-400">
          No webhooks configured.
        </p>
        <ul v-else class="divide-y divide-gray-100 border border-gray-100 rounded">
          <li v-for="wh in form.webhooks" :key="wh.id" class="flex items-center justify-between px-3 py-2">
            <div class="min-w-0">
              <p class="text-xs font-medium text-gray-800 truncate">
                {{ wh.name }}
              </p>
              <p class="text-[11px] text-gray-500 truncate">
                {{ wh.method }} {{ wh.url }}
              </p>
            </div>
            <span :class="['text-[10px] px-1.5 py-0.5 rounded', wh.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500']">
              {{ wh.active ? 'active' : 'off' }}
            </span>
          </li>
        </ul>
      </fieldset>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, reactive, ref, watch } from 'vue'
import Sortable from 'sortablejs'
import EditorIcon from './EditorIcon.vue'

const props = defineProps({
  // Serialized WebsiteForm from the editor payload (id, slug, name, fields,
  // settings, webhooks).
  form: { type: Object, required: true }
})

// save-fields / save-settings carry the desired payload; the parent persists,
// re-serializes, and the new form object flows back in via :form.
const emit = defineEmits(['close', 'save-fields', 'save-settings'])

const FIELD_TYPES = ['text', 'email', 'phone', 'textarea', 'select', 'radio', 'checkbox', 'checkbox_group', 'consent', 'date', 'time', 'file']
const FIELD_WIDTHS = ['full', 'half', 'third', 'quarter']
const SHOW_IF_OPS = ['eq', 'neq', 'in', 'present', 'blank']
const OPTION_TYPES = ['select', 'radio', 'checkbox_group']
const ADD_PRESETS = [
  { type: 'text', label: 'Text' },
  { type: 'email', label: 'Email' },
  { type: 'phone', label: 'Phone' },
  { type: 'textarea', label: 'Long text' },
  { type: 'select', label: 'Dropdown' },
  { type: 'checkbox', label: 'Checkbox' },
  { type: 'consent', label: 'Consent' }
]

const tabs = [
  { id: 'fields', label: 'Fields' },
  { id: 'settings', label: 'Settings' }
]
const tab = ref('fields')

// ---- Fields (local editable copy) ----
// Each field carries a stable _key for v-for + drag identity (it is NOT sent
// to the server — the server owns the immutable `id`).
let keySeq = 0
function withKey(f) { return { ...f, _key: `f${keySeq++}` } }

const fields = reactive([])
function loadFields() {
  fields.splice(0, fields.length, ...(props.form.fields || []).map((f) => withKey(deepClone(f))))
}
loadFields()
// Reload when the parent swaps in a freshly-saved form (e.g. ids assigned).
watch(() => props.form.id, loadFields)
watch(() => props.form.fields, loadFields)

function deepClone(v) { return JSON.parse(JSON.stringify(v ?? null)) }

const expanded = reactive(new Set())
function toggleExpand(key) { if (expanded.has(key)) expanded.delete(key); else expanded.add(key) }

const multiStep = computed(() => fields.some((f) => f.step != null && f.step !== ''))
const stepCount = computed(() => {
  const steps = fields.filter((f) => f.step != null && f.step !== '').map((f) => Number(f.step))
  return steps.length ? Math.max(...steps) + 1 : 1
})

function needsOptions(field) { return OPTION_TYPES.includes(field.type) }
function hasPlaceholder(field) { return ['text', 'email', 'phone', 'textarea', 'select'].includes(field.type) }

function otherFields(field) {
  return fields.filter((f) => f._key !== field._key && f.id)
}

function onTypeChange(field) {
  if (needsOptions(field) && (!field.options || field.options.length === 0)) {
    field.options = [{ label: 'Option 1', value: 'option_1' }]
  }
  commit()
}

// Serialize the local field list back to the server JSON shape (drop _key,
// drop empty step/show_if). The server reconciles ids (immutable) + positions.
function serializeFields() {
  return fields.map((f) => {
    const out = {
      id: f.id || '',
      type: f.type,
      label: f.label || '',
      required: !!f.required,
      width: f.width || 'full'
    }
    if (f.placeholder) out.placeholder = f.placeholder
    if (f.autocomplete) out.autocomplete = f.autocomplete
    if (f.step != null && f.step !== '') out.step = Number(f.step)
    if (needsOptions(f) && f.options) out.options = f.options.map((o) => ({ value: o.value, label: o.label }))
    if (f.show_if && f.show_if.field) {
      out.show_if = { field: f.show_if.field, op: f.show_if.op || 'eq' }
      if (!['present', 'blank'].includes(out.show_if.op)) out.show_if.value = f.show_if.value ?? ''
    }
    return out
  })
}

function commit() {
  emit('save-fields', serializeFields())
}

// ---- Field mutations ----
function addField(type) {
  const f = withKey({
    id: '',
    type,
    label: defaultLabelFor(type),
    required: false,
    width: 'full'
  })
  if (needsOptions(f)) f.options = [{ label: 'Option 1', value: 'option_1' }]
  fields.push(f)
  expanded.add(f._key)
  commit()
}

function defaultLabelFor(type) {
  switch (type) {
    case 'consent': return 'I consent to be contacted about my inquiry.'
    case 'email': return 'Email Address'
    case 'phone': return 'Phone Number'
    case 'textarea': return 'Message'
    default: return 'New field'
  }
}

function removeField(index) {
  const [removed] = fields.splice(index, 1)
  if (removed) expanded.delete(removed._key)
  commit()
}

function moveField(index, dir) {
  const target = index + dir
  if (target < 0 || target >= fields.length) return
  const [moved] = fields.splice(index, 1)
  fields.splice(target, 0, moved)
  commit()
}

function addOption(field) {
  field.options = field.options || []
  field.options.push({ label: `Option ${field.options.length + 1}`, value: `option_${field.options.length + 1}` })
  commit()
}

function removeOption(field, oi) {
  field.options.splice(oi, 1)
  commit()
}

// Auto-fill the option value from its label when the value is still blank, so
// authors usually only type the label.
function onOptionLabel(field, opt) {
  if (!opt.value) {
    opt.value = String(opt.label || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '')
  }
}

function toggleShowIf(field, on) {
  if (on) field.show_if = { field: '', op: 'eq', value: '' }
  else delete field.show_if
  commit()
}

// ---- Drag reorder (mirrors SectionCard item dragging) ----
const fieldList = ref(null)
let sortable = null
function mountSortable() {
  if (!fieldList.value) return
  if (sortable) sortable.destroy()
  sortable = Sortable.create(fieldList.value, {
    animation: 150,
    handle: '.field-row-handle',
    filter: 'button, input, textarea, select',
    preventOnFilter: false,
    onEnd: () => {
      const order = Array.from(fieldList.value.querySelectorAll('[data-field-row]')).map((el) => el.dataset.fieldRow)
      const next = order.map((k) => fields.find((f) => f._key === k)).filter(Boolean)
      if (next.length === fields.length) { fields.splice(0, fields.length, ...next); commit() }
    }
  })
}
watch(fieldList, () => nextTick(mountSortable))
watch(() => fields.length, () => nextTick(mountSortable))
onBeforeUnmount(() => sortable?.destroy())

// ---- Settings (flattened local view of the nested settings jsonb) ----
const s = reactive({})
function loadSettings() {
  const st = props.form.settings || {}
  const email = st.email_notifications || {}
  const spam = st.spam_protection || {}
  const sms = st.sms || {}
  Object.assign(s, {
    success_message: st.success_message || '',
    success_redirect_url: st.success_redirect_url || '',
    email_enabled: email.enabled !== false,
    recipient_emails: (email.recipient_emails || []).join(', '),
    send_customer_confirmation: email.send_customer_confirmation === true,
    honeypot_enabled: spam.honeypot_enabled !== false,
    turnstile_enabled: spam.turnstile_enabled === true,
    rate_limit_per_ip: spam.rate_limit_per_ip || 5,
    sms_enabled: sms.enabled === true,
    sms_front_desk_number: sms.front_desk_number || '',
    sms_auto_response_enabled: sms.auto_response_enabled === true,
    sms_auto_response_template: sms.auto_response_template || ''
  })
}
loadSettings()
watch(() => props.form.id, loadSettings)
watch(() => props.form.settings, loadSettings)

// Re-nest the flattened view into the settings jsonb shape the server stores.
function saveSettings() {
  emit('save-settings', {
    success_message: s.success_message,
    success_redirect_url: s.success_redirect_url,
    email_notifications: {
      enabled: !!s.email_enabled,
      recipient_emails: String(s.recipient_emails || '').split(',').map((e) => e.trim()).filter(Boolean),
      send_customer_confirmation: !!s.send_customer_confirmation
    },
    spam_protection: {
      honeypot_enabled: !!s.honeypot_enabled,
      turnstile_enabled: !!s.turnstile_enabled,
      rate_limit_per_ip: Number(s.rate_limit_per_ip) || 5
    },
    sms: {
      enabled: !!s.sms_enabled,
      front_desk_number: s.sms_front_desk_number,
      auto_response_enabled: !!s.sms_auto_response_enabled,
      auto_response_template: s.sms_auto_response_template
    }
  })
}
</script>
